import { Phase } from './phases.js';

/**
 * The loop's state machine, as a pure reducer.
 *
 * Two properties matter more than anything else here, and both are tested:
 *
 *  1. **No dead ends.** Every phase can reach the Hub. A three-year-old who ends up on a screen
 *     with no way out has, from her point of view, broken the game permanently.
 *  2. **Illegal events are ignored, never thrown.** She will double-press, press two buttons at
 *     once, and press the pedal during a cutscene. An unexpected event returns the *same state
 *     object*, so callers can detect a no-op by identity and simply do nothing.
 */

export interface MissionDef {
  readonly id: string;
  readonly itemId: string;
  /**
   * Which *kind* of drive this is - a recipe, not a route.
   *
   * It used to name one of two hand-authored routes, which meant she had seen everywhere the game had by
   * the fifth session. A recipe plus the seed carried in `LoopState.routeSeed` is a thousand drives.
   */
  readonly recipeId: string;
  /*
   * There was a `weatherId` here, set to 'sunny' on every mission ever written, and nothing read it - the
   * same dead string `RouteDef.biome` was until M8. Weather belongs to the *route*: it is grown from the same
   * seed as the ground and the trees, which is what makes the way home the way out, and a mission is now only
   * ever "fetch this thing, from that kind of place". See `src/core/world/weather.ts`.
   */
  /** Unlock id required before this mission can be offered, or null if always available. */
  readonly requiredUnlock: string | null;
  readonly rewardPoints: number;
}

export interface LoopState {
  readonly phase: Phase;
  /** The car being driven, or coloured. Null in the shell phases. */
  readonly carId: string | null;
  readonly missionId: string | null;
  /**
   * How many of the load are aboard.
   *
   * Was a boolean, when there was one thing to fetch at the far end of the route. Now the trailer holds
   * several, and this is what the HUD fills a pip for and what decides whether the way home is open.
   */
  readonly collected: number;
  /**
   * How many there are to fetch. The way home unlocks when `collected` reaches it.
   *
   * Zero outside a mission, which is honest rather than convenient - and the reducer clamps to at least
   * one before comparing, so a state that should never occur in the game still cannot strand her.
   *
   * It arrives with `BRIEFING_ACCEPTED` rather than being derived here, because the count is a property
   * of the route: `RouteDef.items.length`, grown from the same `routeSeed`. One source of truth, and no
   * way for the number she is shown to disagree with the number actually lying on the road.
   */
  readonly itemCount: number;
  /**
   * The number the whole world is grown from.
   *
   * Carried in the state rather than derived where it is used, because **both drive legs must generate the
   * same world**. Deriving it in the drive screen from the save would work today - mission history is only
   * written at the reward screen, after both legs - but that is an accident of ordering rather than a
   * guarantee, and if it ever changed the return leg would silently become a different place. A test pins
   * it: the way home has the same seed as the way out.
   */
  readonly routeSeed: number;
  /**
   * Where the back paw goes when that is not the Hub - the one level of "where she came from" the
   * game needs.
   *
   * The paw used to go home from everywhere, which is why the Draw studio grew a private closure to
   * fake a step back to its chooser, and why re-colouring a car from the Garage dropped her at the
   * Lookout afterwards. Both were the same missing fact.
   *
   * It is deliberately one level and not a stack: the game is two levels deep by design
   * (`docs/MENUS.md`), and a stack that can never be more than one entry long is a stack that will
   * one day be two and put her somewhere nobody designed. It is cleared the moment it is used, so
   * the paw can never walk her round a loop.
   */
  readonly returnTo: Phase | null;
}

export type LoopEvent =
  | { readonly type: 'NEW_CAR' }
  | { readonly type: 'OPEN_GARAGE' }
  | { readonly type: 'OPEN_COLLECTION' }
  /**
   * The parent's way in, and the only event she cannot produce with a press.
   *
   * The Lookout dispatches it after a three-second hold in one corner; the lock's second half - the
   * sequence - is inside the screen itself rather than a condition on this event, because a state machine
   * that can be forced by the dev overlay is not where a lock belongs.
   */
  | { readonly type: 'OPEN_PARENT' }
  | { readonly type: 'PICK_CAR'; readonly carId: string }
  | { readonly type: 'RECOLOUR'; readonly carId: string }
  | { readonly type: 'FREE_PLAY'; readonly carId: string; readonly routeSeed: number }
  | { readonly type: 'DRAW_DONE'; readonly carId: string }
  | {
      readonly type: 'BRIEFING_ACCEPTED';
      readonly missionId: string;
      readonly routeSeed: number;
      readonly itemCount: number;
    }
  | { readonly type: 'ITEM_COLLECTED' }
  | { readonly type: 'COLLECT_BEAT_DONE' }
  | { readonly type: 'REACHED_HOME' }
  | { readonly type: 'ARRIVE_BEAT_DONE' }
  | { readonly type: 'REWARD_DONE' }
  | { readonly type: 'BACK' };

export type LoopEventType = LoopEvent['type'];

export const INITIAL_STATE: LoopState = {
  phase: Phase.Hub,
  carId: null,
  missionId: null,
  collected: 0,
  itemCount: 0,
  routeSeed: 0,
  returnTo: null,
};

/** Returning to the Hub always clears the in-flight mission but remembers the car. */
function toHub(state: LoopState): LoopState {
  return {
    phase: Phase.Hub,
    carId: state.carId,
    missionId: null,
    collected: 0,
    itemCount: 0,
    routeSeed: 0,
    returnTo: null,
  };
}

/**
 * The only screens the paw may step back to.
 *
 * A whitelist rather than "whatever `returnTo` says", because `returnTo` is data and data can be
 * wrong - the dev overlay forces states by hand, and a save-restored state could name anything. A
 * drive or a celebration beat is not a place she came *from*, and stepping back into one would be a dead
 * end with a phase change to hide it.
 */
const STEP_BACK_TO: readonly Phase[] = [Phase.Garage, Phase.Collection];

/**
 * One press of the paw: back to where she came from if that is a real place, otherwise home.
 *
 * `returnTo` is cleared on arrival, so the next press always reaches the Hub. That is what keeps the
 * no-dead-ends guarantee to two presses rather than to an argument about cycles.
 */
function stepBack(state: LoopState): LoopState {
  const target = state.returnTo;
  if (target === null || target === state.phase || !STEP_BACK_TO.includes(target)) return toHub(state);
  return {
    phase: target,
    carId: state.carId,
    missionId: null,
    collected: 0,
    itemCount: 0,
    routeSeed: 0,
    returnTo: null,
  };
}

export function reduce(state: LoopState, event: LoopEvent): LoopState {
  // The big paw in the corner works from everywhere except the Lookout, which is the one screen with
  // nothing behind it.
  if (event.type === 'BACK') {
    return state.phase === Phase.Hub ? state : stepBack(state);
  }

  switch (state.phase) {
    case Phase.Hub:
      // Everything from the Hub leaves `returnTo` null: the Hub *is* where the paw goes by default,
      // so there is nothing to remember.
      switch (event.type) {
        case 'NEW_CAR':
          return {
            phase: Phase.Draw,
            carId: null,
            missionId: null,
            collected: 0,
            itemCount: 0,
            routeSeed: 0,
            returnTo: null,
          };
        case 'OPEN_GARAGE':
          return { ...state, phase: Phase.Garage, returnTo: null };
        case 'OPEN_COLLECTION':
          return { ...state, phase: Phase.Collection, returnTo: null };
        case 'OPEN_PARENT':
          return { ...state, phase: Phase.Parent, returnTo: null };
        case 'PICK_CAR':
          return {
            phase: Phase.Briefing,
            carId: event.carId,
            missionId: null,
            collected: 0,
            itemCount: 0,
            routeSeed: 0,
            returnTo: null,
          };
        case 'FREE_PLAY':
          return {
            phase: Phase.FreePlay,
            carId: event.carId,
            missionId: null,
            collected: 0,
            itemCount: 0,
            // Free play has no return leg, so a fresh number every time costs nothing and gives variety.
            routeSeed: event.routeSeed,
            returnTo: null,
          };
        default:
          return state;
      }

    case Phase.Collection:
      // Nothing to do here but look, choose a hat, and go back - which the back paw handles above.
      return state;

    case Phase.Parent:
      /*
       * Everything on the parent screen is a setting, a file, or a wipe - none of which is a phase change.
       * The way out is the same `BACK` as everywhere else, handled above, and `returnTo` is null on the way
       * in, so it lands at the Lookout: the screen she was on is also the only screen it can be reached
       * from.
       */
      return state;

    case Phase.Garage:
      switch (event.type) {
        case 'PICK_CAR':
          return {
            phase: Phase.Briefing,
            carId: event.carId,
            missionId: null,
            collected: 0,
            itemCount: 0,
            routeSeed: 0,
            // The paw on the PupPad means "not this car after all", which is the Garage, not the Hub.
            returnTo: Phase.Garage,
          };
        case 'FREE_PLAY':
          return {
            phase: Phase.FreePlay,
            carId: event.carId,
            missionId: null,
            collected: 0,
            itemCount: 0,
            routeSeed: event.routeSeed,
            // Leaving a drive always lands at the Lookout, wherever the drive was started from: the
            // paw there means "I have finished driving", and the shelf of cars is not what she was
            // looking at.
            returnTo: null,
          };
        case 'NEW_CAR':
          return {
            phase: Phase.Draw,
            carId: null,
            missionId: null,
            collected: 0,
            itemCount: 0,
            routeSeed: 0,
            returnTo: Phase.Garage,
          };
        // Re-enters the Draw studio with an existing car loaded, so she can change its colours
        // without losing it. The Draw screen distinguishes the two cases by carId being non-null.
        case 'RECOLOUR':
          return {
            phase: Phase.Draw,
            carId: event.carId,
            missionId: null,
            collected: 0,
            itemCount: 0,
            routeSeed: 0,
            returnTo: Phase.Garage,
          };
        default:
          return state;
      }

    case Phase.Draw:
      if (event.type === 'DRAW_DONE') {
        /*
         * Painting a new car flows into a mission - that *is* the loop. Re-colouring one does not:
         * she came to change its colours, and being launched on an errand for a car she already had
         * is the game deciding what she wanted.
         *
         * Entering the studio *with* a car is what distinguishes the two, which is the same test the
         * Draw screen itself uses to decide whether to show the chooser (`findExistingCar`).
         */
        if (state.carId !== null) return stepBack({ ...state, carId: event.carId });

        return {
          phase: Phase.Briefing,
          carId: event.carId,
          missionId: null,
          collected: 0,
          itemCount: 0,
          routeSeed: 0,
          returnTo: null,
        };
      }
      return state;

    case Phase.Briefing:
      if (event.type === 'BRIEFING_ACCEPTED') {
        /*
         * The seed lands here and is never touched again until she is back at the Hub. Everything from here
         * to the reward screen - both drive legs - grows the same world from it.
         *
         * The count rides along with it for the same reason: the briefing has already grown the route from
         * this seed to find out how many things are lying on it, and asking a second time somewhere else is
         * how the number she is shown and the number on the road come to disagree.
         */
        return {
          ...state,
          phase: Phase.DriveOut,
          missionId: event.missionId,
          collected: 0,
          itemCount: event.itemCount,
          routeSeed: event.routeSeed,
          // Once she is driving, the paw means "I have finished", and that is the Lookout however she
          // got here. Carrying the Garage into a drive would put the paw somewhere she cannot see
          // from the road.
          returnTo: null,
        };
      }
      return state;

    case Phase.DriveOut:
      if (event.type === 'ITEM_COLLECTED') {
        /*
         * Only the last one leaves the drive.
         *
         * An intermediate pickup returns a *new* state object with the *same* phase, which is the whole
         * trick: the router notifies its listeners and the HUD fills a pip, but `render` sees the phase
         * matched and does not swap screens. A phase change here would tear down the Pixi app and the
         * Planck world and set her back down at the Lookout, three times in one drive.
         *
         * `itemCount` is clamped to at least one before comparing. It is zero outside a mission, and a
         * state forced by hand - the dev overlay, a save from a schema that does not exist yet - carrying
         * a zero into a drive would otherwise make this a phase `ITEM_COLLECTED` could never leave. The
         * back paw would still get her out, but "still" is not the standard this file holds itself to.
         */
        const need = Math.max(1, state.itemCount);
        const collected = Math.min(need, state.collected + 1);
        // The load is already complete: the identical object, so a duplicate latch is a no-op by identity.
        if (collected === state.collected) return state;
        return collected >= need
          ? { ...state, phase: Phase.Collected, collected }
          : { ...state, collected };
      }
      return state;

    case Phase.Collected:
      if (event.type === 'COLLECT_BEAT_DONE') return { ...state, phase: Phase.DriveBack };
      return state;

    case Phase.DriveBack:
      if (event.type === 'REACHED_HOME') return { ...state, phase: Phase.Arrived };
      return state;

    case Phase.Arrived:
      if (event.type === 'ARRIVE_BEAT_DONE') return { ...state, phase: Phase.Reward };
      return state;

    case Phase.Reward:
      if (event.type === 'REWARD_DONE') return toHub(state);
      return state;

    case Phase.FreePlay:
      // Free play has no goal and no end; the only way out is the back paw, handled above.
      return state;

    default:
      return state;
  }
}

/**
 * Pick the next mission.
 *
 * `rand` is injected rather than taken from Math.random so mission selection is reproducible in
 * tests and in the input record/replay harness - the same reason terrain takes a seed. The purity
 * checker (scripts/check-core-purity.mjs) enforces that no module under src/core reaches for
 * Math.random directly.
 *
 * Avoids the most recent missions so she does not get "fetch the ball" three times running, but
 * degrades gracefully rather than returning null when every available mission is recent.
 *
 * And then avoids the most recent *places*, which is the stronger of the two rules. A mission id is an item
 * crossed with a recipe, so three different items fetched from three town errands counted as three fresh
 * missions and read - correctly - as the same drive three times: the place fills the screen and the item is
 * a small icon in the corner of the PupPad. Both filters are preferences rather than constraints, applied
 * narrowest last and each dropped if it would leave nothing.
 */
export function chooseMission(
  pool: readonly MissionDef[],
  opts: {
    readonly unlocks: readonly string[];
    readonly recentMissionIds: readonly string[];
    readonly rand: () => number;
    /** How many recent missions to avoid repeating. */
    readonly avoidLast?: number;
    /** How many recent *recipes* to avoid revisiting. Fewer, because there are fewer of them. */
    readonly avoidLastRecipes?: number;
  },
): MissionDef | null {
  const available = pool.filter(
    (m) => m.requiredUnlock === null || opts.unlocks.includes(m.requiredUnlock),
  );
  if (available.length === 0) return null;

  const avoidLast = opts.avoidLast ?? 3;
  const recent = new Set(opts.recentMissionIds.slice(-avoidLast));
  const fresh = available.filter((m) => !recent.has(m.id));
  const candidates = fresh.length > 0 ? fresh : available;

  // Looked up in the whole pool, not in `available`: a mission she played before an unlock changed is still
  // a place she has just been.
  const avoidLastRecipes = opts.avoidLastRecipes ?? 2;
  const recentRecipes = new Set(
    opts.recentMissionIds
      .slice(-avoidLastRecipes)
      .map((id) => pool.find((m) => m.id === id)?.recipeId)
      .filter((id): id is string => id !== undefined),
  );
  const elsewhere = candidates.filter((m) => !recentRecipes.has(m.recipeId));
  const shortlist = elsewhere.length > 0 ? elsewhere : candidates;

  return shortlist[randomIndex(shortlist.length, opts.rand)] ?? null;
}

/** Clamped so a rand() implementation returning exactly 1 (or NaN) cannot index out of bounds. */
function randomIndex(length: number, rand: () => number): number {
  const r = rand();
  if (!Number.isFinite(r)) return 0;
  return Math.min(length - 1, Math.max(0, Math.floor(r * length)));
}
